import { FollowerTemplateSchema, type FollowerTemplate } from '../../schemas/follower';

/**
 * Template del **Supporto Elementale del Druido** (manuale V1, rr. 3174-3190).
 *
 * 3° template Follower del sistema unificato FI-8 (dopo Compagno Animale del
 * Ranger in FI-8a e Famiglio del Mago in FI-8b). Esercita per la prima volta:
 *
 *  - **`recruitRoll`** (D8a): tira+SAG al reclutamento → 10+ concede 4 scelte
 *    di peculiarità, 7-9 ne concede 2. `pickCount` del gruppo `main` runtime
 *    via {@link getEffectivePickCount}.
 *  - **`peculiaritiesFromChoiceGroup`** con 5 mapping eterogenei, di cui 2
 *    nuovi kind D8a:
 *      - `hp-bonus-scaling` («resistente»: +2 PF×lvl, max +20)
 *      - `cost-suspension` («ben disposto»: il Druido non paga il Costo)
 *  - **`archetypeTagBundle`** (D8a, intrinseco): l'elemento scelto popola
 *    `Follower.stats.tags` con `*fatto-di-X*`, `*immune-a-X*`,
 *    `*vulnerabile-a-Y*` (manuale r. 3180).
 *  - **`costMotivation`** «Ricevere un degno sacrificio o un favore» —
 *    sospendibile via peculiarità `ben-disposto`.
 *
 * **Mapping elemento ↔ opposto** (manuale r. 3180 — coppie classiche):
 *  - fuoco ↔ acqua
 *  - terra ↔ aria
 *
 * **Stats fisse** (manuale r. 3178): livello 2, modificatori tutti a +1,
 * 1 PF (hpRule fixed), Carico 6, d6. Limite "1 elementale per volta" è
 * description-only nel testo della mossa (runtime Fase F).
 *
 * Niente `capabilityCaps`: il bonus PF di «resistente» è già cappato dallo
 * stesso effect (`max: 20`); le altre peculiarità modificano stat discrete
 * (cap da `StatModifierSchema`).
 */
export const druidoSupportoElementaleTemplate: FollowerTemplate = FollowerTemplateSchema.parse({
  id: 'druido-supporto-elementale',
  sourceMoveId: 'druido.supporto-elementale',
  type: 'summon',
  label: 'Supporto Elementale',
  baseStats: {
    level: 2,
    modifiers: {
      forza: 1,
      destrezza: 1,
      costituzione: 1,
      intelligenza: 1,
      saggezza: 1,
      carisma: 1,
    },
    hpRule: { type: 'fixed', value: 1 },
    damageDie: 'd6',
    load: 6,
    tags: [],
  },
  fixedSkills: [],
  fixedTraits: [],
  costMotivation: 'Ricevere un degno sacrificio o un favore',
  archetypeFromChoiceGroup: 'elemento',
  archetypeTagBundle: {
    fromChoiceGroup: 'elemento',
    optionToTags: {
      fuoco: ['fatto-di-fuoco', 'immune-a-fuoco', 'vulnerabile-a-acqua'],
      acqua: ['fatto-di-acqua', 'immune-a-acqua', 'vulnerabile-a-fuoco'],
      terra: ['fatto-di-terra', 'immune-a-terra', 'vulnerabile-a-aria'],
      aria: ['fatto-di-aria', 'immune-a-aria', 'vulnerabile-a-terra'],
    },
  },
  recruitRoll: {
    stat: 'saggezza',
    onSuccess: { choicesCount: 4 },
    onPartial: { choicesCount: 2 },
  },
  peculiaritiesFromChoiceGroup: {
    fromChoiceGroup: 'main',
    optionToEffect: {
      potente: {
        kind: 'modifier-from-narrative',
        narrativeFieldId: 'quale-mod-potente',
        baseAmount: 2,
      },
      resistente: {
        kind: 'hp-bonus-scaling',
        perCharacterLevel: 2,
        max: 20,
      },
      'ben-disposto': {
        kind: 'cost-suspension',
      },
      'tiene-istinti': {
        kind: 'narrative-trait',
        traitId: 'elementale-controllo-istinti',
        label: 'Tiene sotto controllo gli istinti primordiali',
        description: "L'elementale tiene a freno la sua natura caotica mentre esegue gli ordini.",
      },
      'capacita-utile': {
        kind: 'custom-trait-from-narrative',
        labelFieldId: 'capacita-utile-label-1',
        descriptionFieldId: 'capacita-utile-desc-1',
        labelFieldIdSecond: 'capacita-utile-label-2',
        descriptionFieldIdSecond: 'capacita-utile-desc-2',
      },
    },
  },
  nameFromNarrativeField: 'nome-elementale',
  respawnRule:
    "Se l'elementale raggiunge 0 PF (o se il Costo non è pagato), si ribella e si libera dal patto. Una nuova evocazione richiede un nuovo tiro a *Supporto Elementale*.",
});
